'use client';

import { checkIsAdminRole, type IUserSession } from '@repo/auth';
import {
  NavUser,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
} from '@repo/ui';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import { Link } from '@/components/link';
import { useSignOut } from '../../hooks/use-sign-out';
import { getAccountSidebarData, getDashboardSidebarData, getMySidebarData } from './data';
import { getUserDetails } from './helper/get-user-details';
import { NavMainControlled } from './nav-main-controlled';
import { SidebarNavSecondary } from './sidebar-nav-secondary';
import { SidebarNavTools } from './sidebar-nav-tools';

export function DashboardSidebar({
  initialSession,
}: {
  initialSession: IUserSession | null;
}): React.ReactNode {
  const pathname = usePathname();
  const handleSignOut = useSignOut();

  const sidebarData = useMemo(() => {
    const userDetails = getUserDetails(initialSession);
    const isAdmin = checkIsAdminRole({ role: initialSession?.user?.role });
    const isSysRoute = pathname?.startsWith('/sys');
    const isAccountRoute = pathname?.startsWith('/account');
    if (isSysRoute) {
      return getDashboardSidebarData({
        user: userDetails,
        isAdmin,
        pathname: pathname ?? undefined,
      });
    }
    if (isAccountRoute) {
      return getAccountSidebarData({ user: userDetails, isAdmin });
    }
    return getMySidebarData({ user: userDetails, isAdmin });
  }, [initialSession, pathname]);

  const { branding, navMain, navSecondary, navTools, user } = sidebarData;

  return (
    <Sidebar
      variant="inset"
      collapsible="offcanvas"
      className="bg-sidebar select-none"
      style={{ backgroundColor: 'hsl(var(--sidebar))' }}
    >
      {branding && (
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <div className="flex w-full items-center justify-between">
                <SidebarMenuButton
                  asChild
                  className="data-[slot=sidebar-menu-button]:!p-1.5 flex-1"
                >
                  <Link href={branding.href}>
                    <branding.logo className="!size-5" />
                    <span className="text-base font-semibold">{branding.title}</span>
                  </Link>
                </SidebarMenuButton>
                <SidebarTrigger className="ml-2 hidden md:flex" />
              </div>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
      )}
      <SidebarContent>
        <NavMainControlled items={navMain} />
        {navTools && navTools.length > 0 && <SidebarNavTools items={navTools} />}
        <SidebarNavSecondary items={navSecondary ?? []} pathname={pathname} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={user} onSignOut={handleSignOut} />
      </SidebarFooter>
    </Sidebar>
  );
}
